import { Button, OtpInput, Skeleton } from '@bennows/boows-react';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api, call } from '../../lib/api';
import { useToast } from '../../lib/toasts';
import { QrCode } from './QrCode';
import { CODE_LENGTH, useCodeStep } from './useCodeStep';

/**
 * First sign-in without an authenticator (REQ-140-004): a new secret as QR
 * code and as text, then its first code confirms it and verifies the session.
 */
export function TotpSetupStep({ onVerified, onStartOver }: { readonly onVerified: () => void; readonly onStartOver: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const setup = useMutation({ mutationFn: () => call(() => api.POST('/v1/operator/me/totp')) });
  const step = useCodeStep(
    (code) => call(() => api.POST('/v1/operator/me/totp/confirmation', { body: { code } })),
    () => {
      toast(t('totpSetup.done'));
      onVerified();
    },
  );
  const messageRef = useRef<HTMLParagraphElement>(null);
  const started = useRef(false);
  const start = setup.mutate;

  // One secret per visit: a second call would replace the one on screen (StrictMode runs effects twice).
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    start();
  }, [start]);
  useEffect(() => {
    if (step.failure) messageRef.current?.focus();
  }, [step.failure]);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    step.submit();
  };

  return (
    <>
      <h1>{t('totpSetup.title')}</h1>
      <p className="ui-auth__lead">{t('totpSetup.lead')}</p>
      {setup.isError ? (
        <div className="ui-error-state" role="alert">
          <p className="ui-error-state__text">{t('totpSetup.error')}</p>
          <Button variant="secondary" onClick={() => setup.mutate()}>
            {t('common.retry')}
          </Button>
        </div>
      ) : setup.data ? (
        <div className="ui-console-totp">
          <QrCode value={setup.data.uri} label={t('totpSetup.qr')} />
          <p className="ui-console-totp__manual">{t('totpSetup.manual')}</p>
          <code className="ui-console-totp__secret">{setup.data.secret}</code>
          <a className="ui-auth__link" href={setup.data.uri}>
            {t('totpSetup.openApp')}
          </a>
        </div>
      ) : (
        <div className="ui-console-totp" aria-busy="true">
          <Skeleton variant="block" className="ui-console-qr" />
          <span className="ui-visually-hidden">{t('totpSetup.loading')}</span>
        </div>
      )}
      <form onSubmit={onSubmit} noValidate>
        <OtpInput
          label={t('signIn.code')}
          value={step.code}
          onChange={step.setCode}
          onComplete={(value) => step.submit(value)}
          length={CODE_LENGTH}
          groupSize={3}
          disabled={!setup.data || step.pending}
          invalid={step.incomplete || step.failure !== null}
          help={step.incomplete ? t('signIn.codeIncomplete') : undefined}
        />
        {step.failure ? (
          <p className="ui-auth__message" role="alert" tabIndex={-1} ref={messageRef}>
            {t(step.failure)}
          </p>
        ) : null}
        <Button type="submit" loading={step.pending} disabled={!setup.data} block>
          {t('totpSetup.confirm')}
        </Button>
      </form>
      <Button variant="ghost" onClick={onStartOver}>
        {t('signIn.startOver')}
      </Button>
    </>
  );
}
